/** @jsx jsx */
import { useEffect, useCallback, type ReactNode } from 'react';

import { css, jsx } from '@compiled/react';
import InlineDialog from '@atlaskit/inline-dialog'; // ignore-for-ENGHEALTH-17759
import Portal from '@atlaskit/portal';
import { token } from '@atlaskit/tokens';
import { useViewport } from '@atlassian/timeline-table/common/context/viewport/context';
import { OVERLAY_FLYOUT_Z_INDEX } from '../../constants/z-index.tsx';
import type { Position } from '../../types/common.tsx';

export type Props = {
	/**
	 * location coordinates (x / y) where to place the popup in relative to viewport with "top" / "left" attriubes and a "fixed" position in the flyout component
	 */
	position: Position;
	/**
	 * Content to show in dialog popup overlay
	 */
	content: ReactNode;
	/**
	 * (Optional) data testId attribute to attach to wrapping flyout popup container
	 */
	testId?: string;
	/**
	 * Reference to wrapper element where active focus is kept
	 */
	triggerRef: { current: HTMLElement | null };
	/**
	 * (Optional) event handler callback to run when the popup overlay container is closed
	 *
	 */
	onClose?: () => void;
	/**
	 * (Optional) Reference to the dialog content where focus needs to be moved
	 *
	 */
	openFocusRef?: { current: HTMLElement | null };
};

/* The overlay flyout will be rendered on a Portal with a fixed position.
 * It needs to close on scroll, otherwise appear to move away from its trigger.
 */
const OverlayFlyout = ({ position, content, testId, onClose, triggerRef, openFocusRef }: Props) => {
	const { listenToViewportScroll, stopListeningToViewportScroll } = useViewport();

	const onCloseInternal = useCallback(() => {
		stopListeningToViewportScroll(onCloseInternal);
		onClose?.();
	}, [onClose, stopListeningToViewportScroll]);

	const onCloseDialog = useCallback(
		({ event }: { event: Event }) => {
			if (
				// call onClose if the trigger is not defined
				triggerRef?.current === null ||
				// call onClose if click outside the trigger
				!(
					triggerRef?.current &&
					event.target instanceof Node &&
					triggerRef?.current.contains(event.target)
				)
			) {
				onCloseInternal();
				triggerRef?.current?.focus();
			}
		},
		[onCloseInternal, triggerRef],
	);

	useEffect(() => {
		listenToViewportScroll(onCloseInternal);

		return () => {
			stopListeningToViewportScroll(onCloseInternal);
		};
	}, [listenToViewportScroll, onCloseInternal, stopListeningToViewportScroll]);

	useEffect(() => {
		setTimeout(() => {
			openFocusRef?.current?.focus();
		}, 0);
	}, [openFocusRef, triggerRef]);

	return (
		<Portal zIndex={OVERLAY_FLYOUT_Z_INDEX}>
			<div css={inlineDialogWrapperStyles} {...(testId ? { 'data-testid': testId } : {})}>
				<InlineDialog isOpen content={content} onClose={onCloseDialog}>
					<div
						css={anchorStyles}
						style={{
							left: position.x,
							top: position.y,
						}}
					/>
				</InlineDialog>
			</div>
		</Portal>
	);
};

const defaultProps: Partial<Props> = {
	triggerRef: { current: null },
	openFocusRef: { current: null },
};

OverlayFlyout.defaultProps = defaultProps;

export { OverlayFlyout };

// Child selector is for the nested inline dialog
const inlineDialogWrapperStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> div': {
		padding: 0,
		overflow: 'hidden',
		boxShadow: token('elevation.shadow.overlay', '0 2px 4px 0 rgba(0, 0, 0, 0.4)'),
	},
});

const anchorStyles = css({
	position: 'fixed',
});
